/**
 * CreatePOModal — Create Stock Purchase Order from an order's purchases.
 * Adapted from amazon/StockPOModal for the Order Hub.
 */
import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import {
  X, Loader2, Check, Package, Trash2, AlertCircle,
} from 'lucide-react';
import type { UnifiedOrder } from './types';

interface POItem {
  asin: string;
  title: string;
  qty_ordered: number;
  unit_cost: number;
  purchase_id: number;
}

interface CreatePOModalProps {
  order: UnifiedOrder;
  sellerId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreatePOModal({ order, sellerId, onClose, onSuccess }: CreatePOModalProps) {
  const mlOrderId = order.ml_order_id || order.pack_id;

  // Build items from purchases that have data
  const [items, setItems] = useState<POItem[]>(
    order.purchases
      .filter(p => p.asin && !p.stock_po_id) // only purchases without existing PO
      .map(p => ({
        asin: p.asin,
        title: order.items.find(i => i.seller_sku === p.asin)?.title || p.asin,
        qty_ordered: 1,
        unit_cost: Number(p.unit_cost) || 0,
        purchase_id: p.id,
      }))
  );
  const [trackingNumber, setTrackingNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [autoConfirm, setAutoConfirm] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const totalCost = items.reduce((s, i) => s + i.qty_ordered * i.unit_cost, 0);
  const totalQty = items.reduce((s, i) => s + i.qty_ordered, 0);

  const updateItem = (idx: number, field: string, value: any) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeItem = (idx: number) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      setError('Se requiere al menos 1 item');
      return;
    }
    setSaving(true);
    setError('');

    const res = await api.post('/amazon/po', {
      seller_id: sellerId,
      purchase_ids: items.map(i => i.purchase_id),
      expected_date: expectedDate || null,
      notes: notes || null,
    });

    if (res.success) {
      onSuccess();
    } else {
      setError((res as any).error || 'Error creando Stock PO');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Package size={18} className="text-[var(--color-primary)]" />
            <h3 className="font-bold text-[var(--color-text)]">
              Crear Stock PO {'\u2014'} Orden #{mlOrderId}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]">
            <X size={18} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
            </div>
          )}

          {/* Summary KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3 text-center">
              <p className="text-xs text-[var(--color-text-tertiary)]">Items</p>
              <p className="text-lg font-bold text-[var(--color-text)]">{items.length}</p>
            </div>
            <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3 text-center">
              <p className="text-xs text-[var(--color-text-tertiary)]">Total Qty</p>
              <p className="text-lg font-bold text-[var(--color-text)]">{totalQty}</p>
            </div>
            <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3 text-center">
              <p className="text-xs text-[var(--color-text-tertiary)]">Total Cost</p>
              <p className="text-lg font-bold text-red-500">${totalCost.toFixed(2)}</p>
            </div>
          </div>

          {/* Items table */}
          {items.length === 0 ? (
            <div className="text-center py-8 text-[var(--color-text-tertiary)] border border-[var(--color-border)] rounded-lg">
              <Package size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No hay compras disponibles para crear PO</p>
              <p className="text-xs mt-1">Las compras ya tienen PO asignada o no tienen ASIN</p>
            </div>
          ) : (
            <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--color-bg-tertiary)] text-xs text-[var(--color-text-tertiary)] uppercase">
                    <th className="text-left p-2">ASIN</th>
                    <th className="text-center p-2 w-20">Qty</th>
                    <th className="text-right p-2 w-28">Unit Cost</th>
                    <th className="text-right p-2 w-24">Subtotal</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx} className="border-t border-[var(--color-border)]">
                      <td className="p-2">
                        <div>
                          <p className="font-mono text-xs text-teal-500">{item.asin}</p>
                          <p className="text-[10px] text-[var(--color-text-tertiary)] truncate max-w-[200px]">{item.title}</p>
                        </div>
                      </td>
                      <td className="p-2 text-center">
                        <input type="number" value={item.qty_ordered} min={1}
                          onChange={(e) => updateItem(idx, 'qty_ordered', parseInt(e.target.value) || 1)}
                          className="w-16 px-1 py-0.5 text-center bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-xs text-[var(--color-text-tertiary)]">$</span>
                          <input type="number" step="0.01" value={item.unit_cost}
                            onChange={(e) => updateItem(idx, 'unit_cost', parseFloat(e.target.value) || 0)}
                            className="w-20 px-1 py-0.5 text-right bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
                        </div>
                      </td>
                      <td className="p-2 text-right font-mono text-xs text-[var(--color-text)]">
                        ${(item.qty_ordered * item.unit_cost).toFixed(2)}
                      </td>
                      <td className="p-2">
                        <button onClick={() => removeItem(idx)}
                          className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-text-tertiary)]">Tracking Number</label>
              <input type="text" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="1Z999..."
                className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm font-mono text-[var(--color-text)]" />
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-tertiary)]">Expected Date</label>
              <input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)}
                className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
            </div>
          </div>

          <div>
            <label className="text-xs text-[var(--color-text-tertiary)]">Notas</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas opcionales..."
              className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
          </div>

          {/* Auto confirm toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={autoConfirm} onChange={(e) => setAutoConfirm(e.target.checked)}
              className="w-4 h-4 rounded accent-[var(--color-primary)]" />
            <span className="text-sm text-[var(--color-text)]">Auto-confirmar (status=confirmed, ajustar qty_incoming)</span>
          </label>

          {/* Submit */}
          <button onClick={handleSubmit} disabled={saving || items.length === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            Crear Stock PO ({items.length} items {'\u2014'} ${totalCost.toFixed(2)})
          </button>
        </div>
      </div>
    </div>
  );
}
